import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Skeleton,
  Button,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  format,
  subDays,
  subMonths,
  startOfMonth,
  startOfYear,
  startOfQuarter,
} from "date-fns";
import { DayPicker, DateRange } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ChevronDown } from "lucide-react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MaterialIcons from "../../../assests/MaterialIcons/MaterialIcons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { resellingOverviewStatsAction } from "../../../redux/actions";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "../../TeamDetails/TeamDetails.scss";
import ResellingClients from "./ResellingClients";

// Register ChartJS modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const formatCentsToCurrency = (cents?: number) => {
  if (cents === undefined || cents === null) return "$0";
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatMonthLabel = (monthStr: string) => {
  if (!monthStr) return "";
  const parts = monthStr.split("-");
  if (parts.length === 2) {
    const monthIndex = parseInt(parts[1], 10) - 1;
    const date = new Date(parseInt(parts[0], 10), monthIndex, 1);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString("en-US", { month: "short" });
    }
  }
  return monthStr;
};

const ResellingOverview: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [selectedLabel, setSelectedLabel] = useState<string>("All time");

  const { overviewData, overviewLoading } = useSelector(
    (state: RootState) => state.reselling,
  );

  useEffect(() => {
    const payload: any = {};
    if (startDate) {
      payload.date_from = format(new Date(startDate), "yyyy-MM-dd");
    }
    if (endDate) {
      payload.date_to = format(new Date(endDate), "yyyy-MM-dd");
    }
    dispatch(resellingOverviewStatsAction(payload));
  }, [dispatch, startDate, endDate]);

  const openCalendar = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const closeCalendar = () => setAnchorEl(null);

  const handleStartDateChange = (value: DateRange | undefined) => {
    setStartDate(value?.from ?? null);
    setEndDate(value?.to ?? null);
  };

  const applyShortcut = (type: string, label: string) => {
    const today = new Date();
    let newRange: DateRange;

    switch (type) {
      case "today":
        newRange = { from: today, to: today };
        break;

      case "last7":
        newRange = { from: subDays(today, 6), to: today };
        break;

      case "last4w":
        newRange = { from: subDays(today, 27), to: today };
        break;

      case "last6m":
        newRange = { from: subMonths(today, 6), to: today };
        break;

      case "last12m":
        newRange = { from: subMonths(today, 12), to: today };
        break;

      case "mtd":
        newRange = { from: startOfMonth(today), to: today };
        break;

      case "qtd":
        newRange = { from: startOfQuarter(today), to: today };
        break;

      case "ytd":
        newRange = { from: startOfYear(today), to: today };
        break;

      case "all":
        newRange = { from: undefined, to: undefined };
        break;

      default:
        return;
    }

    setSelectedLabel(label);
    handleStartDateChange(newRange);
    setRange(newRange);
    closeCalendar();
  };

  const display =
    selectedLabel && selectedLabel !== "All time"
      ? selectedLabel
      : startDate && endDate
        ? `${format(startDate, "MMM dd, yyyy")} - ${format(endDate, "MMM dd, yyyy")}`
        : startDate
          ? `${format(startDate, "MMM dd, yyyy")} -`
          : endDate
            ? `${format(endDate, "MMM dd, yyyy")} -`
            : "";

  const displaySubtitle = selectedLabel
    ? selectedLabel.toLowerCase()
    : startDate && endDate
      ? `${format(startDate, "MMM dd, yyyy")} - ${format(endDate, "MMM dd, yyyy")}`.toLowerCase()
      : "all time";

  // Chart setup
  const isLast7Days =
    selectedLabel === "Last 7 days" ||
    (startDate &&
      endDate &&
      Math.abs(endDate.getTime() - startDate.getTime()) <=
        7 * 24 * 60 * 60 * 1000);

  const isLast4Weeks =
    selectedLabel === "Last 4 weeks" ||
    (startDate &&
      endDate &&
      Math.abs(endDate.getTime() - startDate.getTime()) >
        7 * 24 * 60 * 60 * 1000 &&
      Math.abs(endDate.getTime() - startDate.getTime()) <=
        31 * 24 * 60 * 60 * 1000);

  const trendData = overviewData?.profit_trend || [];

  let finalChartLabels: string[] = [];
  let finalChartValues: number[] = [];

  if (isLast4Weeks) {
    finalChartLabels = ["Wk 1", "Wk 2", "Wk 3", "Wk 4"];
    if (trendData.length === 4) {
      finalChartValues = trendData.map((t: any) => (t.profit_cents || 0) / 100);
    } else if (trendData.length > 0) {
      const values = trendData.map((t: any) => (t.profit_cents || 0) / 100);
      finalChartValues = [
        ...values,
        ...Array(Math.max(0, 4 - values.length)).fill(0),
      ].slice(0, 4);
    } else {
      finalChartValues = [0, 0, 0, 0];
    }
  } else if (isLast7Days) {
    finalChartLabels = ["Wk 1", "Wk 2"];
    if (trendData.length === 2) {
      finalChartValues = trendData.map((t: any) => (t.profit_cents || 0) / 100);
    } else if (trendData.length > 0) {
      const values = trendData.map((t: any) => (t.profit_cents || 0) / 100);
      finalChartValues = [
        ...values,
        ...Array(Math.max(0, 2 - values.length)).fill(0),
      ].slice(0, 2);
    } else {
      finalChartValues = [0, 0];
    }
  } else {
    if (trendData.length > 0) {
      finalChartLabels = trendData.map((t: any) => formatMonthLabel(t.month));
      finalChartValues = trendData.map((t: any) => (t.profit_cents || 0) / 100);
    } else {
      finalChartLabels = ["Feb", "Mar", "Apr", "May", "Jun", "Jul"];
      finalChartValues = [0, 0, 0, 0, 0, 0];
    }
  }

  const lineChartData = {
    labels: finalChartLabels,
    datasets: [
      {
        label: "Profit ($)",
        data: finalChartValues,
        borderColor: "#5F9B20",
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);
          gradient.addColorStop(0, "rgba(95, 155, 32, 0.2)");
          gradient.addColorStop(1, "rgba(95, 155, 32, 0.0)");
          return gradient;
        },
        fill: true,
        tension: 0.35,
        borderWidth: 2.5,
        pointBackgroundColor: "#5F9B20",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const lineChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#1F2937",
        titleFont: { family: "var(--font-family-manrope)", size: 12 },
        bodyFont: {
          family: "var(--font-family-manrope)",
          size: 14,
          weight: "bold",
        },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context: any) => `$${context.raw.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#9CA3AF",
          font: {
            family: "var(--font-family-manrope)",
            size: 12,
          },
        },
      },
      y: {
        grid: {
          color: "#F3F4F6",
          drawBorder: false,
        },
        ticks: {
          color: "#9CA3AF",
          font: {
            family: "var(--font-family-manrope)",
            size: 12,
          },
          callback: (val: any) => `$${val}`,
        },
        beginAtZero: true,
      },
    },
  };

  // Profit by source percentages calculated based on total_profit_cents and Map Check-Ins
  const totalProfitCents = overviewData?.total_profit_cents ?? 0;
  const creditsCents = overviewData?.profit_by_source?.credits_cents ?? 0;
  const rawCheckinsCents = overviewData?.profit_by_source?.checkins_cents;

  const checkinsCents =
    rawCheckinsCents !== undefined && rawCheckinsCents !== null
      ? rawCheckinsCents
      : Math.max(0, totalProfitCents - creditsCents);

  const totalSourceCents = totalProfitCents || checkinsCents + creditsCents;

  const checkinsPct =
    totalSourceCents > 0
      ? Math.min(100, Math.round((checkinsCents / totalSourceCents) * 100))
      : 0;
  const creditsPct =
    totalSourceCents > 0
      ? Math.min(100, Math.round((creditsCents / totalSourceCents) * 100))
      : 0;

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 0. Date Range Filter Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            color: "#6B7280",
            fontFamily: "var(--font-family-manrope)",
            fontWeight: 400,
          }}
        >
          Showing {display || "All time"}
        </Typography>

        <Button
          onClick={openCalendar}
          sx={{
            backgroundColor: display ? "#E3F2FD" : "#FFFFFF",
            border: "1px solid",
            borderColor: display ? "#1A73E8" : "#D1D5DB",
            borderRadius: "8px",
            px: 2,
            py: 0.8,
            minHeight: "38px",
            fontSize: "14px",
            fontWeight: 500,
            color: display ? "#1A73E8" : "#111827",
            textTransform: "none",
            fontFamily: "var(--font-family-manrope)",
            display: "inline-flex",
            alignItems: "center",
            gap: 1.2,
            whiteSpace: "nowrap",
            boxShadow: display
              ? "0 1px 2px rgba(26,115,232,0.12)"
              : "0 1px 2px rgba(0,0,0,0.03)",
            "&:hover": {
              backgroundColor: display ? "#D0E7FC" : "#F9FAFB",
              borderColor: display ? "#1557B0" : "#9CA3AF",
            },
          }}
        >
          <CalendarMonthIcon
            style={{ fontSize: "18px", color: display ? "#1A73E8" : "#6B7280" }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: display ? "#1A73E8" : "#111827",
              fontFamily: "var(--font-family-manrope)",
              whiteSpace: "nowrap",
            }}
          >
            {display || "All time"}
          </Typography>
          <ChevronDown
            size={16}
            color={display ? "#1A73E8" : "#6B7280"}
            style={{ flexShrink: 0 }}
          />
        </Button>
      </Box>

      {/* CALENDAR POPOVER matching BusinessDashboardPage:L3119-L3333 */}
      <Popover
        elevation={0}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "var(--medium-border-radius)",
              border: "1px solid #d8d8d8",
              mt: 1,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              md: "row",
            },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 160 },
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <List
              sx={{
                width: {
                  xs: "100%",
                  md: 160,
                },
                borderRight: {
                  xs: "none",
                  md: "1px solid #e0e0e0",
                },
                display: "flex",
                flexDirection: {
                  xs: "row",
                  md: "column",
                },
                overflowX: {
                  xs: "auto",
                  md: "visible",
                },
                whiteSpace: {
                  xs: "nowrap",
                  md: "normal",
                },
                gap: {
                  xs: 1.5,
                  md: 0.5,
                },
                padding: {
                  xs: "8px 6px",
                  md: "8px",
                },

                scrollBehavior: "smooth",

                "&::-webkit-scrollbar": {
                  height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#bfbfbf",
                  borderRadius: 4,
                },
              }}
            >
              {[
                { label: "Today", key: "today" },
                { label: "Last 7 days", key: "last7" },
                { label: "Last 4 weeks", key: "last4w" },
                { label: "Last 6 months", key: "last6m" },
                { label: "Last 12 months", key: "last12m" },
                { label: "Month to date", key: "mtd" },
                { label: "Quarter to date", key: "qtd" },
                { label: "Year to date", key: "ytd" },
                { label: "All time", key: "all" },
              ].map(({ label, key }) => (
                <ListItemButton
                  key={key}
                  selected={selectedLabel === label}
                  sx={{
                    borderRadius: "var(--small-border-radius)",
                    flexShrink: 0,
                    padding: "6px 12px",
                    "&.Mui-selected": {
                      backgroundColor: "#E8F0FE !important",
                      color: "#1a73e8 !important",
                      fontWeight: 400,
                      "& .MuiTypography-root": {
                        color: "#1a73e8 !important",
                        fontWeight: "400 !important",
                      },
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "#E8F0FE !important",
                      "& .MuiTypography-root": {
                        color: "#1a73e8 !important",
                        fontWeight: "400 !important",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={() => applyShortcut(key, label)}
                >
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      fontFamily: "var(--font-family-manrope)",
                      fontWeight: 400,
                      color: selectedLabel === label ? "#1a73e8" : "#374151",
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
          <Box
            sx={{
              p: 2,
              "& .rdp-day_button": { fontWeight: 400, fontSize: "14px" },
              "& .rdp-selected .rdp-day_button": {
                fontWeight: 400,
                fontSize: "14px",
              },
              "& .rdp-range_start .rdp-day_button": {
                fontWeight: 400,
                fontSize: "14px",
              },
              "& .rdp-range_end .rdp-day_button": {
                fontWeight: 400,
                fontSize: "14px",
              },
              "& .rdp-range_middle .rdp-day_button": {
                fontWeight: 400,
                fontSize: "14px",
              },
            }}
          >
            <DayPicker
              mode="range"
              numberOfMonths={2}
              selected={range}
              disabled={{ after: new Date() }}
              onSelect={(value) => {
                setRange(value);
                setSelectedLabel("");
              }}
            />
            <Box display="flex" justifyContent="flex-end" mt={1} gap={1}>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: "var(--action-button-radius) !important",
                  fontFamily: "var(--font-family-manrope)",
                  fontSize: "12px",
                  textTransform: "none",
                  transition: "all 0.2s ease-in-out",

                  "&:hover": {
                    opacity: 0.9,
                  },

                  "&.Mui-disabled": {
                    opacity: 0.6,
                    cursor: "not-allowed",
                  },

                  "@media (max-width:480px)": {
                    fontSize: "14px",
                  },
                }}
                onClick={() => {
                  setRange({ from: undefined, to: undefined });
                  setStartDate(null);
                  setEndDate(null);
                  setSelectedLabel("All time");
                  closeCalendar();
                }}
              >
                Clear
              </Button>

              <Button
                variant="contained"
                disableElevation
                sx={{
                  backgroundColor:
                    "var(--action-button-background-color, #1a73e8) !important",
                  color: "#fff !important",
                  borderRadius: "var(--action-button-radius) !important",
                  fontFamily: "var(--font-family-manrope)",
                  fontSize: "12px",
                  textTransform: "none",
                  transition: "all 0.2s ease-in-out",

                  "&:hover": {
                    backgroundColor:
                      "var(--action-button-background-color, #1a73e8)",
                    opacity: 0.9,
                  },

                  "&.Mui-disabled": {
                    opacity: 0.6,
                    cursor: "not-allowed",
                  },

                  "@media (max-width:480px)": {
                    fontSize: "14px",
                  },
                }}
                onClick={() => {
                  handleStartDateChange(range);
                  if (range?.from && range?.to) closeCalendar();
                  else if (!range?.from && !range?.to) closeCalendar();
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>

      {/* 1. Top Metrics Bar */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          mb: 4,
          overflow: "hidden",
        }}
      >
        <Grid container>
          {/* Card 1: TOTAL PROFIT / MO */}
          <Grid
            size={{ xs: 12, sm: 6, md: 3 }}
            sx={{
              p: 3,
              borderRight: {
                md: "1px solid #E5E7EB",
                sm: "1px solid #E5E7EB",
                xs: "none",
              },
              borderBottom: {
                xs: "1px solid #E5E7EB",
                sm: "1px solid #E5E7EB",
                md: "none",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#6B7280",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily: "var(--font-family-manrope)",
                mb: 1,
              }}
            >
              Total Profit / Mo
            </Typography>
            {overviewLoading ? (
              <Skeleton width={120} height={42} />
            ) : (
              <Typography
                sx={{
                  fontSize: "32px",
                  fontWeight: 700,
                  color: "#5F9B20",
                  fontFamily: "var(--font-family-manrope)",
                  lineHeight: 1.2,
                }}
              >
                {formatCentsToCurrency(overviewData?.total_profit_cents)}
              </Typography>
            )}
          </Grid>

          {/* Card 2: RESELLER REVENUE / MO */}
          <Grid
            size={{ xs: 12, sm: 6, md: 3 }}
            sx={{
              p: 3,
              borderRight: { md: "1px solid #E5E7EB", xs: "none" },
              borderBottom: {
                xs: "1px solid #E5E7EB",
                sm: "1px solid #E5E7EB",
                md: "none",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#6B7280",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily: "var(--font-family-manrope)",
                mb: 1,
              }}
            >
              Reseller Revenue / Mo
            </Typography>
            {overviewLoading ? (
              <Skeleton width={120} height={42} />
            ) : (
              <Typography
                sx={{
                  fontSize: "32px",
                  fontWeight: 700,
                  color: "#111827",
                  fontFamily: "var(--font-family-manrope)",
                  lineHeight: 1.2,
                }}
              >
                {formatCentsToCurrency(overviewData?.reseller_revenue_cents)}
              </Typography>
            )}
          </Grid>

          {/* Card 3: ACTIVE LOCATIONS */}
          <Grid
            size={{ xs: 12, sm: 6, md: 3 }}
            sx={{
              p: 3,
              borderRight: {
                md: "1px solid #E5E7EB",
                sm: "1px solid #E5E7EB",
                xs: "none",
              },
              borderBottom: { xs: "1px solid #E5E7EB", md: "none" },
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#6B7280",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily: "var(--font-family-manrope)",
                mb: 1,
              }}
            >
              Active Locations
            </Typography>
            {overviewLoading ? (
              <Skeleton width={80} height={42} />
            ) : (
              <Typography
                sx={{
                  fontSize: "32px",
                  fontWeight: 700,
                  color: "#111827",
                  fontFamily: "var(--font-family-manrope)",
                  lineHeight: 1.2,
                }}
              >
                {(overviewData?.active_locations ?? 0).toLocaleString()}
              </Typography>
            )}
          </Grid>

          {/* Card 4: CREDITS RESOLD / MO */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#6B7280",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily: "var(--font-family-manrope)",
                mb: 1,
              }}
            >
              Credits Resold / Mo
            </Typography>
            {overviewLoading ? (
              <Skeleton width={100} height={42} />
            ) : (
              <Typography
                sx={{
                  fontSize: "32px",
                  fontWeight: 700,
                  color: "#111827",
                  fontFamily: "var(--font-family-manrope)",
                  lineHeight: 1.2,
                }}
              >
                {(overviewData?.credits_resold ?? 0).toLocaleString()}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* 2. Middle Section: Reseller profit & Profit by source */}
      <Grid container spacing={4} sx={{ mb: 5 }}>
        {/* Left Column: Reseller profit */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#111827",
                fontFamily: "var(--font-family-manrope)",
              }}
            >
              Reseller profit
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6B7280",
                fontFamily: "var(--font-family-manrope)",
                mb: 2.5,
              }}
            >
              Your spread over {displaySubtitle}.
            </Typography>

            {overviewLoading ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={240}
                sx={{ borderRadius: 2 }}
              />
            ) : (
              <Box sx={{ height: 240, width: "100%" }}>
                <Line data={lineChartData} options={lineChartOptions} />
              </Box>
            )}
          </Box>
        </Grid>

        {/* Right Column: Profit by source */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#111827",
                fontFamily: "var(--font-family-manrope)",
              }}
            >
              Profit by source
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6B7280",
                fontFamily: "var(--font-family-manrope)",
                mb: 3,
              }}
            >
              Where your margin comes from.
            </Typography>

            {overviewLoading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Map Check-Ins */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#111827",
                        fontFamily: "var(--font-family-manrope)",
                      }}
                    >
                      Map Check-Ins
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#111827",
                        fontFamily: "var(--font-family-manrope)",
                      }}
                    >
                      {formatCentsToCurrency(checkinsCents)}
                    </Typography>
                  </Box>
                  {/* Progress track */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      backgroundColor: "#E5E7EB",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${checkinsPct}%`,
                        height: "100%",
                        backgroundColor: "#2563EB",
                        borderRadius: 4,
                        transition: "width 0.5s ease-in-out",
                      }}
                    />
                  </Box>
                </Box>

                {/* AI credits */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#111827",
                        fontFamily: "var(--font-family-manrope)",
                      }}
                    >
                      AI credits
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#111827",
                        fontFamily: "var(--font-family-manrope)",
                      }}
                    >
                      {formatCentsToCurrency(
                        overviewData?.profit_by_source?.credits_cents,
                      )}
                    </Typography>
                  </Box>
                  {/* Progress track */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      backgroundColor: "#E5E7EB",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${creditsPct}%`,
                        height: "100%",
                        backgroundColor: "#5F9B20",
                        borderRadius: 4,
                        transition: "width 0.5s ease-in-out",
                      }}
                    />
                  </Box>
                </Box>

                {/* Blended margin */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#6B7280",
                      fontFamily: "var(--font-family-manrope)",
                    }}
                  >
                    Blended margin
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#5F9B20",
                      fontFamily: "var(--font-family-manrope)",
                    }}
                  >
                    {overviewData?.blended_margin_percent ?? 0}%
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* 3. Bottom Section: Client accounts */}
      <Box sx={{ mt: 4 }}>
        <ResellingClients startDate={startDate} endDate={endDate} />
      </Box>
    </Box>
  );
};

export default ResellingOverview;
